import { getOrganizationGoogleTagManagerApiKey } from '../../settings/organizationSettings';
import { getAuthData } from '../auth';

const CATEGORIES = {
  ACTION: 'action',
  CARD: 'card',
  FORM: 'form',
  HEADER: 'header',
  POPUP: 'popup',
  ROWITEM: 'rowitem',
  TAB: 'tab',
  TABLE: 'table',
  USER: 'user',
  PAGE: 'page',
  USER_IDENTIFICATION: 'user identification',
};

const trackEvent = (...args) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag(...args);
  }
};

const initGoogleTagManager = () => {
  trackEvent('config', getOrganizationGoogleTagManagerApiKey());
};

initGoogleTagManager();

function getCustomDimensions() {
  const dimensions = {};
  const authData = getAuthData();

  if (authData && authData.role) {
    dimensions.permission_level = authData.role;
  }

  return dimensions;
}

export function trackPage() {
  trackEvent('event', 'page_view', {
    event_category: CATEGORIES.PAGE,
    event_label: document.title,
    ...getCustomDimensions(),
  });
}

export function trackUserLogin({ role, profile }) {
  trackEvent('event', 'identify', {
    permission_level: role,
    event_category: CATEGORIES.USER_IDENTIFICATION,
    ...getCustomDimensions(),
  });

  trackEvent('event', 'Login', {
    non_interaction: true,
    event_category: CATEGORIES.USER,
    event_label: `${profile.name} (${profile.email?.replace('@', '-')}) logged in`,
    ...getCustomDimensions(),
  });
}

export function trackUserLogout(profile) {
  trackEvent('event', 'Logout', {
    non_interaction: true,
    event_category: CATEGORIES.USER,
    event_label: `${profile.name} (${profile.email?.replace('@', '-')}) logged out due to inactivity`,
    ...getCustomDimensions(),
  });
}

export function promoteOpenGAEvent() {
  trackEvent('event', 'promote_open', {
    event_category: CATEGORIES.POPUP,
    event_label: 'Promote',
    ...getCustomDimensions(),
  });
}

export function breadcrumbHomeIconClickedGAEvent() {
  trackEvent('event', 'breadcrumb_home_icon_clicked', {
    event_category: CATEGORIES.HEADER,
    ...getCustomDimensions(),
  });
}

export function leftNavOrgIconClickedGAEvent() {
  trackEvent('event', 'left_nav_org_icon_clicked', {
    event_category: CATEGORIES.HEADER,
    ...getCustomDimensions(),
  });
}

export function pressedInterviewHeaderGAEvent() {
  trackEvent('event', 'pressed_interview_header', {
    event_category: CATEGORIES.HEADER,
    ...getCustomDimensions(),
  });
}

export function pressedInterviewInterviewDetailsSectionGAEvent() {
  trackEvent('event', 'pressed_interview_interview_details_section', {
    event_category: CATEGORIES.HEADER,
    ...getCustomDimensions(),
  });
}

export function pressedBackgroundCheckExtLinkGAEvent() {
  trackEvent('event', 'pressed_background_check_ext_link', {
    event_category: CATEGORIES.CARD,
    ...getCustomDimensions(),
  });
}

export function pressedFormsExtLinkGAEvent() {
  trackEvent('event', 'pressed_forms_ext_link', {
    event_category: CATEGORIES.CARD,
    ...getCustomDimensions(),
  });
}

function expandSectionGAEvent(prefix, postfix) {
  const eventName = `${prefix ? `${prefix}_` : ''}expand_section${postfix ? `_${postfix}` : ''}`;
  trackEvent('event', eventName, {
    event_category: CATEGORIES.CARD,
    ...getCustomDimensions(),
  });
}

function minimizeSectionGAEvent(prefix, postfix) {
  const eventName = `${prefix ? `${prefix}_` : ''}minimize_section${postfix ? `_${postfix}` : ''}`;
  trackEvent('event', eventName, {
    event_category: CATEGORIES.CARD,
    ...getCustomDimensions(),
  });
}

export function pressedCalFullPickerGAEvent() {
  trackEvent('event', 'pressed_cal_full_picker', {
    event_category: CATEGORIES.HEADER,
    ...getCustomDimensions(),
  });
}

export function pressedCalPrevMonthGAEvent() {
  trackEvent('event', 'pressed_cal_prev_month', {
    event_category: CATEGORIES.HEADER,
    ...getCustomDimensions(),
  });
}

export function pressedCalNextMonthGAEvent() {
  trackEvent('event', 'pressed_cal_next_month', {
    event_category: CATEGORIES.HEADER,
    ...getCustomDimensions(),
  });
}

export function pressedCheckInGAEvent() {
  trackEvent('event', 'pressed_check-in', {
    event_category: CATEGORIES.ROWITEM,
    ...getCustomDimensions(),
  });
}

export function pressedInterviewPageGAEvent() {
  trackEvent('event', 'pressed_interview-page', {
    event_category: CATEGORIES.ROWITEM,
    ...getCustomDimensions(),
  });
}

export function pressedLaunchVideoInterviewGAEvent() {
  trackEvent('event', 'pressed_launch-video-interview', {
    category: CATEGORIES.ROWITEM,
    ...getCustomDimensions(),
  });
}

export function promoteButtonClickOkGAEvent() {
  trackEvent('event', 'promote_button_click_ok', {
    event_category: CATEGORIES.POPUP,
    ...getCustomDimensions(),
  });
}

export function promoteButtonClickCancelGAEvent() {
  trackEvent('event', 'promote_button_click_cancel', {
    event_category: CATEGORIES.POPUP,
    ...getCustomDimensions(),
  });
}

export function interviewButtonClickContinueGAEvent() {
  trackEvent('event', 'interview_button_click_continue', {
    event_category: CATEGORIES.POPUP,
    ...getCustomDimensions(),
  });
}

export function conversionCalculatorOpenGAEvent() {
  trackEvent('event', 'conversion_calculator_open', {
    event_category: CATEGORIES.POPUP,
    ...getCustomDimensions(),
  });
}

export function addSkillButtonClickAddGAEvent() {
  trackEvent('event', 'addskill_button_click_add', {
    event_category: CATEGORIES.ROWITEM,
    ...getCustomDimensions(),
  });
}

export function addskillButtonClickRemoveGAEvent() {
  trackEvent('event', 'addskill_button_click_remove', {
    event_category: CATEGORIES.ROWITEM,
    ...getCustomDimensions(),
  });
}

export function applicantButtonClickSaveGAEvent() {
  trackEvent('event', 'applicant_button_click_save', {
    event_category: CATEGORIES.FORM,
    ...getCustomDimensions(),
  });
}

export function applicantButtonClickCancelGAEvent() {
  trackEvent('event', 'applicant_button_click_cancel', {
    event_category: CATEGORIES.FORM,
    ...getCustomDimensions(),
  });
}

export function editTalentOnAddSkillClickGAEvent() {
  trackEvent('event', 'edit_talent_add_skill_button_click', {
    event_category: CATEGORIES.FORM,
    ...getCustomDimensions(),
  });
}

export function talentCareerOnAddInterestClickGAEvent() {
  trackEvent('event', 'talent_career_add_interest_button_click', {
    event_category: CATEGORIES.POPUP,
    ...getCustomDimensions(),
  });
}

export function talentOnTimeHeaderGraphicHoverGAEvent() {
  trackEvent('event', 'talent_on_time_header_graphic_hover', {
    event_category: CATEGORIES.HEADER,
    ...getCustomDimensions(),
  });
}

export function talentAvgRatingHeaderGraphicHoverGAEvent() {
  trackEvent('event', 'talent_avg_rating_header_graphic_hover', {
    event_category: CATEGORIES.HEADER,
    ...getCustomDimensions(),
  });
}

export function talentWorkedvsCxledHeaderGraphicHoverGAEvent() {
  trackEvent('event', 'talent_worked_vs_cxled_header_graphic_hover', {
    event_category: CATEGORIES.HEADER,
    ...getCustomDimensions(),
  });
}

export function predictionsListOnRowClickGAEvent() {
  trackEvent('event', 'predictions_list_on_row_click', {
    event_category: CATEGORIES.ROWITEM,
    ...getCustomDimensions(),
  });
}

export function predictionsListOnFunnelSegmentwClickGAEvent() {
  trackEvent('event', 'predictions_list_on_funnel_segment_click', {
    event_category: CATEGORIES.ROWITEM,
    ...getCustomDimensions(),
  });
}

export function yourApplicantsListOnExpandRowGAEvent() {
  trackEvent('event', 'your_applicants_list_on_expand_row_click', {
    event_category: CATEGORIES.ROWITEM,
    ...getCustomDimensions(),
  });
}

export function unclosedJobsListOnRowClickGAEvent() {
  trackEvent('event', 'unclosed_jobs_list_on_row_click', {
    event_category: CATEGORIES.ROWITEM,
    ...getCustomDimensions(),
  });
}

export function jobsAtRiskListOnRowClickGAEvent() {
  trackEvent('event', 'jobs_at_risk_list_on_row_click', {
    event_category: CATEGORIES.ROWITEM,
    ...getCustomDimensions(),
  });
}

export function poolsBreakdownListOnSortingChangeGAEvent() {
  trackEvent('event', 'pools_breakdown_list_on_sorting_change', {
    event_category: CATEGORIES.TABLE,
    ...getCustomDimensions(),
  });
}

export function poolsViewModalFunnelTabViewedGAEvent() {
  trackEvent('event', 'pools_view_modal_funnel_tab_viewed', {
    event_category: CATEGORIES.POPUP,
    ...getCustomDimensions(),
  });
}

export function poolsBreakdownListOnPaginationChangeGAEvent() {
  trackEvent('event', 'pools_breakdown_list_on_pagination_change', {
    event_category: CATEGORIES.TABLE,
    ...getCustomDimensions(),
  });
}

export function poolsBreakdownListOnRowClickGAEvent() {
  trackEvent('event', 'pools_breakdown_list_on_row_click', {
    event_category: CATEGORIES.ROWITEM,
    ...getCustomDimensions(),
  });
}

export function poolsBreakdownListOnPoolIconHoverGAEvent() {
  trackEvent('event', 'pools_breakdown_list_on_pool_icon_hover', {
    event_category: CATEGORIES.ROWITEM,
    ...getCustomDimensions(),
  });
}

export function landingPageReorderGAEvent(name, rankBefore, rankAfter) {
  trackEvent('event', 'reorder_section_element', {
    name,
    rankBefore,
    rankAfter,
    ...getCustomDimensions(),
  });
}

export function approvalWorkflowOutstandingTabViewGAEvent() {
  trackEvent('event', 'approval_workflow_outstanding_tab_click', {
    event_category: CATEGORIES.TABLE,
    ...getCustomDimensions(),
  });
}

export function approvalWorkflowResolvedTabViewGAEvent() {
  trackEvent('event', 'approval_workflow_resolved_tab_click', {
    event_category: CATEGORIES.TABLE,
    ...getCustomDimensions(),
  });
}

export function reviewTimingsPopupViewGAEvent() {
  trackEvent('event', 'approval_workflow_review_timings_popup_open', {
    event_category: CATEGORIES.POPUP,
    ...getCustomDimensions(),
  });
}

export function reviewTimingsClosePopupGAEvent() {
  trackEvent('event', 'approval_workflow_review_timings_popup_close', {
    event_category: CATEGORIES.POPUP,
    ...getCustomDimensions(),
  });
}

export function reviewTimingsCancelGAEvent() {
  trackEvent('event', 'approval_workflow_review_timings_cancel_click', {
    event_category: CATEGORIES.POPUP,
    ...getCustomDimensions(),
  });
}

export function reviewTimingsSubmitViewGAEvent() {
  trackEvent('event', 'approval_workflow_review_timings_submit_click', {
    event_category: CATEGORIES.POPUP,
    ...getCustomDimensions(),
  });
}

export function talentPastJobsCalendarViewGAEvent() {
  trackEvent('event', 'talent_past_jobs_calendar_view_click', {
    event_category: CATEGORIES.TAB,
    ...getCustomDimensions(),
  });
}

export function agencyCreateInviteGAEvent() {
  trackEvent('event', 'agency_create_invite', {
    event_category: CATEGORIES.ACTION,
    ...getCustomDimensions(),
  });
}

export function agencyUpdateInviteGAEvent() {
  trackEvent('event', 'agency_update_invite', {
    event_category: CATEGORIES.ACTION,
    ...getCustomDimensions(),
  });
}

export function approveJobGAEvent() {
  trackEvent('event', 'approve_job_click', {
    event_category: CATEGORIES.ACTION,
    ...getCustomDimensions(),
  });
}

export function createBookingForManagerGAEvent() {
  trackEvent('event', 'create_booking_for_manager_action_click', {
    event_category: CATEGORIES.ACTION,
    ...getCustomDimensions(),
  });
}

export function hoverOverRowActionsGAEvent(eventName) {
  trackEvent('event', `${eventName}_row_actions_hover`, {
    event_category: CATEGORIES.ROWITEM,
    ...getCustomDimensions(),
  });
}

export function selectSortingColumnGAEvent(tableName, columnName, order) {
  trackEvent('event', `${tableName}_sort_by_${columnName}_${order}`, {
    event_category: CATEGORIES.TABLE,
    ...getCustomDimensions(),
  });
}

export function poolsViewBulkActionGAEventClick(actionName, columnName, order) {
  trackEvent(
    'event',
    `pools_view_bulk_${actionName}_click${columnName && order ? `_when_${columnName}_${order}` : ''}`,
    {
      event_category: CATEGORIES.ROWITEM,
      ...getCustomDimensions(),
    },
  );
}

export function poolsViewRowItemActionGAEventClick(actionName, columnName, order) {
  trackEvent(
    'event',
    `pools_view_individual_${actionName}_click${columnName && order ? `_when_${columnName}_${order}` : ''}`,
    {
      event_category: CATEGORIES.ROWITEM,
      ...getCustomDimensions(),
    },
  );
}

export default {
  agencyCreateInviteGAEvent,
  agencyUpdateInviteGAEvent,
  trackPage,
  trackUserLogin,
  trackUserLogout,
  promoteOpenGAEvent,
  breadcrumbHomeIconClickedGAEvent,
  leftNavOrgIconClickedGAEvent,
  pressedInterviewHeaderGAEvent,
  pressedInterviewInterviewDetailsSectionGAEvent,
  pressedBackgroundCheckExtLinkGAEvent,
  pressedFormsExtLinkGAEvent,
  expandSectionGAEvent,
  minimizeSectionGAEvent,
  pressedCalFullPickerGAEvent,
  pressedCalPrevMonthGAEvent,
  pressedCalNextMonthGAEvent,
  pressedCheckInGAEvent,
  pressedInterviewPageGAEvent,
  pressedLaunchVideoInterviewGAEvent,
  promoteButtonClickOkGAEvent,
  promoteButtonClickCancelGAEvent,
  interviewButtonClickContinueGAEvent,
  conversionCalculatorOpenGAEvent,
  addSkillButtonClickAddGAEvent,
  addskillButtonClickRemoveGAEvent,
  applicantButtonClickSaveGAEvent,
  applicantButtonClickCancelGAEvent,
  editTalentOnAddSkillClickGAEvent,
  talentCareerOnAddInterestClickGAEvent,
  talentOnTimeHeaderGraphicHoverGAEvent,
  talentAvgRatingHeaderGraphicHoverGAEvent,
  talentWorkedvsCxledHeaderGraphicHoverGAEvent,
  predictionsListOnRowClickGAEvent,
  predictionsListOnFunnelSegmentwClickGAEvent,
  yourApplicantsListOnExpandRowGAEvent,
  unclosedJobsListOnRowClickGAEvent,
  jobsAtRiskListOnRowClickGAEvent,
  poolsBreakdownListOnSortingChangeGAEvent,
  poolsViewModalFunnelTabViewedGAEvent,
  poolsBreakdownListOnPaginationChangeGAEvent,
  poolsBreakdownListOnRowClickGAEvent,
  poolsBreakdownListOnPoolIconHoverGAEvent,
  landingPageReorderGAEvent,
  talentPastJobsCalendarViewGAEvent,
};
